import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import parse, { DOMNode } from 'html-react-parser';
import { Container, IconButton, Link, Stack, Toolbar, Tooltip, Typography, colors, Chip, Box } from '@mui/material';
import {
  Download,
  TextSnippet,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

import { translate as t } from 'hooks/useTranslations';
import useIssuedManual from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import { Policy } from 'views/App/Portal/Policy/_types/policy';
import { ManualChapter } from 'views/App/Portal/Policy/_types/manuals/draft';
import PolicyHeaders from '../_components/PolicyHeaders';
import PolicyAttachments from '../../shared/PolicyAttachments';
import { IssuedManualsContext } from '../context';
import contentConversionHelper from '../../DraftManuals/helpers/contentConversionHelper';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import useAccreditationTags from 'views/App/Portal/AWB/_data_sources/useAccreditationTags';
import SectionTags from './SectionTags';
import { ActionMenu } from 'components/_atoms';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import { AppContext } from 'views/App/context';
import { success, neutral } from '_lex/_foundations/phxColors';
import ExportModal from '../_components/ExportModal';

const IssuedManualsPolicy: React.FC = () => {
  const { id, policyId } = useParams();
  const [policy, setPolicy] = useState<Policy>();
  const [chapter, setChapter] = useState<ManualChapter>();
  const { manualHasPermissions, handleDownload, showAccreditationTags, setShowAccreditationTags } =
    useContext(IssuedManualsContext);
  const { isFeatureEnabled, currentOrganizationId, hasPermission } = useContext(AppContext);
  const manualQuery = useIssuedManual(id || '');
  const [params, setParams] = useSearchParams();
  const [transformedAccreditationTags, setTransformedAccreditationTags] = useState<Record<string, string[]>>({});
  const accreditationTagsQuery = useAccreditationTags(id, policyId);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  useEffect(() => {
    if (manualQuery.data && manualQuery.data.chapters) {
      manualQuery.data.chapters.forEach((chapter) => {
        if (chapter.policies) {
          chapter.policies.forEach((p) => {
            if (p.id === policyId) {
              setPolicy(p);
              setChapter(chapter);
            }
          });
        }
      });
    }
  }, [manualQuery.data, policyId]);

  useEffect(() => {
    if (accreditationTagsQuery.data) {
      const transformed = accreditationTagsQuery.data.reduce(
        (acc, item) => {
          const contentId = item.contentUri.split('/').pop() || '';
          const standardText = `${item.bodyAbbreviation} ${item.bodyVersionAbbreviation} ${item.standardHeading}: ${item.standardTitle}`;

          if (!acc[contentId]) {
            acc[contentId] = [];
          }
          acc[contentId].push(standardText);
          return acc;
        },
        {} as Record<string, string[]>
      );

      setTransformedAccreditationTags(transformed);
    }
  }, [accreditationTagsQuery.data]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const sec = params.get('s');
      if (sec) {
        const el = document.getElementById(`polsec_${sec}`);
        if (el) {
          el.scrollIntoView(true);
        }
      }
    }, 2000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [params]);

  const hasAwbSubscription = useMemo(() => {
    return (
      hasPermission('awbEssentials', currentOrganizationId || '') ||
      hasPermission('awbPremium', currentOrganizationId || '')
    );
  }, [hasPermission, currentOrganizationId]);

  const viewMenuItems = useMemo(() => {
    const actions: ActionMenuItemProps[] = [];

    const handleAccreditationTagsToggle = () => {
      if (setShowAccreditationTags) {
        setShowAccreditationTags(!showAccreditationTags);
      }
    };

    if (hasAwbSubscription) {
      actions.push({
        key: 'accreditationTags',
        label: t('draftManuals.menu.showAccreditationTags'),
        onClick: handleAccreditationTagsToggle,
        icon: showAccreditationTags ? (
          <VisibilityIcon sx={{ color: `${success[700]} !important` }} />
        ) : (
          <VisibilityOffIcon sx={{ color: `${neutral[400]} !important` }} />
        ),
      });
    }

    return actions;
  }, [showAccreditationTags, setShowAccreditationTags, isFeatureEnabled, hasAwbSubscription]);

  const renderPolicyTitle = () => {
    if (!policy) return '';

    const titleText = [policy.key, policy.title].join(': ');

    if (!showAccreditationTags || !hasAwbSubscription || !policyId || !transformedAccreditationTags[policyId]?.length) {
      return titleText;
    }

    const policyTags = transformedAccreditationTags[policyId!];
    const visibleTags = policyTags.slice(0, 2);
    const remainingTags = policyTags.slice(2);

    return (
      <Stack spacing={2}>
        <Typography variant='displaySm'>{titleText}</Typography>
        <Stack
          direction='row'
          spacing={2}
        >
          {visibleTags.map((tag: string, index: number) => (
            <Chip
              key={`policy_tag_${index}`}
              label={tag}
              variant='filled'
              color='info'
              size='small'
            />
          ))}
          {remainingTags.length > 0 && (
            <Tooltip
              title={
                <Box>
                  {remainingTags.map((tag, index) => (
                    <Box key={`remaining_tag_${index}`}>{tag}</Box>
                  ))}
                </Box>
              }
              placement='top'
            >
              <Chip
                label={`+${remainingTags.length}`}
                variant='filled'
                color='info'
                size='small'
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <TakeoverLayout
      backUrl={`../${id}/chapter/${chapter?.id}`}
      pageTitle={policy ? [policy.key, policy.title].join(': ') : ''}
      headerProps={{
        title: renderPolicyTitle(),
        breadcrumbItems: [
          <Link
            key='b1'
            href='..'
          >
            {t('policy.issuedManuals.title')}
          </Link>,
          <Link
            key='b2'
            href={`../${id}`}
          >
            {manualQuery.data?.name}
          </Link>,
          ...(chapter
            ? [
              <Link
                key='b3'
                href={`../${id}/chapter/${chapter.id}`}
              >
                {[chapter.key, chapter.title].join(': ')}
              </Link>,
            ]
            : []),
          <Typography key='b4'>{policy ? [policy.key, policy.title].join(': ') : ''}</Typography>,
        ],
        actions: policy ? (
          <Stack
            direction='row'
            spacing={4}
          >
            {manualHasPermissions(id ?? '', ['createManualAcknowledgement']) && (
              <Tooltip title={t('policy.issuedManuals.actions.newAdhocAcknowledgement')}>
                <IconButton href={`../${id}/policy/${policyId}/new-acknowledgement`}>
                  <TextSnippet />
                </IconButton>
              </Tooltip>
            )}
            <PolicyAttachments policy={policy} />
            <IconButton onClick={() => setExportModalOpen(true)}>
              <Download />
            </IconButton>
            {viewMenuItems.length > 0 && (
              <ActionMenu
                id='policy-view-menu'
                btnText={t('draftManuals.menu.view')}
                items={viewMenuItems}
              />
            )}
          </Stack>
        ) : null,
      }}
    >
      {policy && policy.key && (
        <Stack>
          <Container maxWidth='lg'>
            <Stack spacing={10}>
              {isFeatureEnabled('policy-headers') && policy.headers && policy.headers.length > 0 && (
                <PolicyHeaders
                  headers={policy.headers}
                  translationPrefix='issuedManuals'
                  manualId={id}
                />
              )}
              {policy.sections.map((section) => (
                <Fragment key={`section_${section.id}`}>
                  <Stack
                    id={`polsec_${section.id}`}
                    sx={{
                      backgroundColor: section.id.toString() === params.get('s') ? colors.amber[50] : 'transparent',
                    }}
                  >
                    <Typography variant='h5'>{[section?.key, section?.title].join(': ')}</Typography>
                    <SectionTags
                      sectionId={section.id}
                      accreditationTags={transformedAccreditationTags}
                      hasAwbSubscription={hasAwbSubscription}
                    />
                    <div>
                      {parse(section.content || '', {
                        replace(node: DOMNode, ind: number) {
                          return PolicyContentHelper.handleContent(node, ind);
                        },
                      })}
                    </div>
                  </Stack>
                  {section.subSections.map((subSection) => (
                    <Stack
                      id={`polsec_${subSection.id}`}
                      key={`subsection_${section.id}_${subSection.id}`}
                      sx={{
                        backgroundColor:
                          subSection.id.toString() === params.get('s') ? colors.amber[50] : 'transparent',
                      }}
                    >
                      <Typography variant='h6'>{[subSection.key, subSection.title].join(': ')}</Typography>
                      <SectionTags
                        sectionId={subSection.id}
                        accreditationTags={transformedAccreditationTags}
                        hasAwbSubscription={hasAwbSubscription}
                      />
                      <div>
                        {parse(subSection.content || '', {
                          replace(node: DOMNode, ind: number) {
                            return PolicyContentHelper.handleContent(node, ind);
                          },
                        })}
                      </div>
                    </Stack>
                  ))}
                </Fragment>
              ))}
            </Stack>
          </Container>
        </Stack>
      )}
      {policy && (
        <ExportModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          exportType='content'
          manualId={id!}
          contentId={policyId!}
          contentType='POLICY'
          contentTitle={policy.title}
        />
      )}
    </TakeoverLayout>
  );
};

export default IssuedManualsPolicy;
